import commercialFinal from '../../assets/flight-check/commercial-final.jpg';
import creativeBanner from '../../assets/flight-check/creative-banner.jpg';
import creativeVideo from '../../assets/flight-check/creative-video.jpg';

// FIXME: mock data for the hidden Flight Check pages; replace with the overview tRPC procedures.

export const NETWORKS = ['TikTok', 'Google', 'Meta'] as const;

export const CREATIVE_TYPES = ['Video', 'Image', 'Carousel'] as const;

export type Network = (typeof NETWORKS)[number];

export type CreativeType = (typeof CREATIVE_TYPES)[number];

export const FUNNEL_STAGES = ['Awareness', 'Consideration', 'Conversion'] as const;

export type FunnelStage = (typeof FUNNEL_STAGES)[number];

export interface NetworkScore {
  network: Network;
  score: number;
}

export interface BatchCreative {
  name: string;
  score: number;
  thumbnailUrl: string;
}

export interface LatestBatch {
  name: string;
  ranAt: string;
  overall: number;
  networks: NetworkScore[];
  creatives: BatchCreative[];
}

export interface Kpis {
  creativesAnalyzed: number;
  batchCount: number;
  averageScore: number;
  latestBatchScore: number;
  strongCount: number;
  needsAttentionCount: number;
}

export interface BreakdownColumn {
  label: string;
  network?: Network;
  score: number;
  strongPercent: number;
  moderatePercent: number;
  weakPercent: number;
}

export interface TrendPoint {
  ranAt: string;
  score: number;
}

export interface WeakestCategory {
  name: string;
  percent: number;
}

export interface NetworkRow {
  network: Network;
  score: number;
  bestPractice: number;
  performance: number;
  brandGuidelines: number;
  weakest: WeakestCategory;
  lowestCreative: { score: number; thumbnailUrl: string };
  type: CreativeType;
}

export interface BatchSummary {
  id: string;
  funnelStage: FunnelStage;
  name: string;
  ranAt: string;
  creativeCount: number;
  rows: NetworkRow[];
}

export interface LatestBatchStrip {
  name: string;
  ranAt: string;
  creativeCount: number;
  overall: number;
  bestPractice: number;
  performance: number;
  brandGuidelines: number;
  weakest: WeakestCategory;
  networks: NetworkScore[];
  needsAttention: number;
}

export interface DashboardMock {
  kpis: Kpis;
  latestBatch: LatestBatch;
  breakdown: { platforms: BreakdownColumn[]; types: BreakdownColumn[] };
  trend: { insight: string; points: TrendPoint[] };
}

export interface AnalysisMock {
  latest: LatestBatchStrip;
  batches: BatchSummary[];
}

export const DASHBOARD_MOCK: DashboardMock = {
  kpis: {
    creativesAnalyzed: 65,
    batchCount: 3,
    averageScore: 6.9,
    latestBatchScore: 6.0,
    strongCount: 16,
    needsAttentionCount: 14,
  },
  latestBatch: {
    name: 'My batch name',
    ranAt: '2027-08-11T18:14:00',
    overall: 6.0,
    networks: [
      { network: 'TikTok', score: 7.3 },
      { network: 'Google', score: 6.0 },
      { network: 'Meta', score: 4.8 },
    ],
    creatives: [
      { name: 'Creative Banner', score: 8.4, thumbnailUrl: creativeBanner },
      { name: 'commercial-final', score: 7.5, thumbnailUrl: commercialFinal },
      { name: 'Creative Video', score: 6.1, thumbnailUrl: creativeVideo },
      { name: 'image180x180', score: 4.3, thumbnailUrl: creativeBanner },
      { name: 'vertical-banner_test', score: 3.8, thumbnailUrl: creativeBanner },
    ],
  },
  breakdown: {
    platforms: [
      {
        label: 'TikTok',
        network: 'TikTok',
        score: 7.6,
        strongPercent: 32,
        moderatePercent: 52,
        weakPercent: 16,
      },
      {
        label: 'Google',
        network: 'Google',
        score: 6.8,
        strongPercent: 20,
        moderatePercent: 55,
        weakPercent: 25,
      },
      {
        label: 'Meta',
        network: 'Meta',
        score: 6.2,
        strongPercent: 14,
        moderatePercent: 52,
        weakPercent: 34,
      },
    ],
    types: [
      { label: 'Video', score: 7.1, strongPercent: 28, moderatePercent: 54, weakPercent: 18 },
      { label: 'Image', score: 6.6, strongPercent: 19, moderatePercent: 53, weakPercent: 28 },
      { label: 'Carousel', score: 6.3, strongPercent: 15, moderatePercent: 55, weakPercent: 30 },
    ],
  },
  trend: {
    insight: 'Your latest batch dipped to 6.0. Two creatives under 5.0 pulled it down.',
    points: [
      { ranAt: '2027-07-17T16:02:00', score: 6.8 },
      { ranAt: '2027-07-28T10:23:00', score: 7.1 },
      { ranAt: '2027-08-11T18:14:00', score: 6.0 },
    ],
  },
};

export const ANALYSIS_MOCK: AnalysisMock = {
  latest: {
    name: 'My batch name',
    ranAt: '2027-08-11T18:14:00',
    creativeCount: 5,
    overall: 6.0,
    bestPractice: 5.8,
    performance: 6.1,
    brandGuidelines: 6.5,
    weakest: { name: 'Story', percent: 32 },
    networks: [
      { network: 'TikTok', score: 7.3 },
      { network: 'Google', score: 6.0 },
      { network: 'Meta', score: 4.8 },
    ],
    needsAttention: 2,
  },
  batches: [
    {
      id: 'my-batch-name',
      funnelStage: 'Conversion',
      name: 'My batch name',
      ranAt: '2027-08-11T18:14:00',
      creativeCount: 5,
      rows: [
        {
          network: 'TikTok',
          score: 7.3,
          bestPractice: 7.1,
          performance: 7.4,
          brandGuidelines: 7.8,
          weakest: { name: 'Story', percent: 41 },
          lowestCreative: { score: 5.1, thumbnailUrl: creativeBanner },
          type: 'Image',
        },
        {
          network: 'Google',
          score: 6.0,
          bestPractice: 5.8,
          performance: 6.1,
          brandGuidelines: 6.5,
          weakest: { name: 'Story', percent: 32 },
          lowestCreative: { score: 3.6, thumbnailUrl: creativeBanner },
          type: 'Image',
        },
        {
          network: 'Meta',
          score: 4.8,
          bestPractice: 4.6,
          performance: 4.9,
          brandGuidelines: 5.3,
          weakest: { name: 'Story', percent: 24 },
          lowestCreative: { score: 2.7, thumbnailUrl: creativeBanner },
          type: 'Image',
        },
      ],
    },
    {
      id: 'back-to-school-promotion',
      funnelStage: 'Consideration',
      name: 'Back to school promotion',
      ranAt: '2027-07-28T10:23:00',
      creativeCount: 34,
      rows: [
        {
          network: 'TikTok',
          score: 7.8,
          bestPractice: 7.6,
          performance: 7.9,
          brandGuidelines: 8.3,
          weakest: { name: 'Story', percent: 66 },
          lowestCreative: { score: 5.9, thumbnailUrl: creativeVideo },
          type: 'Video',
        },
        {
          network: 'Google',
          score: 7.0,
          bestPractice: 6.8,
          performance: 7.1,
          brandGuidelines: 7.5,
          weakest: { name: 'Story', percent: 58 },
          lowestCreative: { score: 4.8, thumbnailUrl: commercialFinal },
          type: 'Image',
        },
        {
          network: 'Meta',
          score: 6.5,
          bestPractice: 6.3,
          performance: 6.6,
          brandGuidelines: 7.0,
          weakest: { name: 'Story', percent: 42 },
          lowestCreative: { score: 4.1, thumbnailUrl: creativeVideo },
          type: 'Video',
        },
      ],
    },
    {
      id: 'black-friday-teasers',
      funnelStage: 'Awareness',
      name: 'Black Friday teasers',
      ranAt: '2027-07-17T16:02:00',
      creativeCount: 26,
      rows: [
        {
          network: 'TikTok',
          score: 7.5,
          bestPractice: 7.3,
          performance: 7.6,
          brandGuidelines: 8.0,
          weakest: { name: 'Product', percent: 49 },
          lowestCreative: { score: 5.6, thumbnailUrl: creativeVideo },
          type: 'Video',
        },
        {
          network: 'Google',
          score: 6.7,
          bestPractice: 6.5,
          performance: 6.8,
          brandGuidelines: 7.2,
          weakest: { name: 'Product', percent: 38 },
          lowestCreative: { score: 4.4, thumbnailUrl: commercialFinal },
          type: 'Image',
        },
        {
          network: 'Meta',
          score: 6.2,
          bestPractice: 6.0,
          performance: 6.3,
          brandGuidelines: 6.7,
          weakest: { name: 'Product', percent: 36 },
          lowestCreative: { score: 3.9, thumbnailUrl: creativeVideo },
          type: 'Video',
        },
      ],
    },
  ],
};
